import './styles/graphic.scss';
import * as d3 from 'd3';
import { scaleSqrt, scalePow } from 'd3-scale';
import { select } from 'd3-selection';
import {
  forceSimulation, forceCollide, forceX, forceY,
} from 'd3-force';
import 'd3-transition';
import { classify } from './utils/helpers';

export default function (props, container, isMobile, isWide) {
  const {
    initialWidth,
    initialHeight,
    data,
    marketCap,
  } = props;


  const nodeColors = {
    nvidia: '#f2f2f2',
    inner: '#dde6e4',
    satellite: '#f8f2e7',
    country: '#c5dae8',
  };
  const nodeStrokes = {
    nvidia: '#53a796',
    inner: '#53a796',
    satellite: '#f8c153',
    country: '#5194c3',
  };
  const linkColors = {
    inner: '#53a796',
    satellite: '#f8c153',
    country: '#5194c3',
  };

  const backgroundColor = '#FEFEFE';
  const nodeHighlightColor = '#F18851';


  function parseDealAmount(amountValue) {
    // if (!amountStr || amountStr.includes('Not disclosed')) return null;
    // const match = amountStr.match(/\$?([\d,.]+)\s*(billion|million)?/i);
    // if (!match) return null;
    // let value = parseFloat(match[1].replace(/,/g, ''));
    // if (match[2] && match[2].toLowerCase() === 'million') {
    //   value /= 1000;
    // }

    if (typeof amountValue === 'string') return amountValue;

    const division = {
      trillion: 1000000000000,
      billion: 1000000000,
      million: 1000000,
    };

    const decimalPlaces = amountValue / 1000000000000 > 0.999 ? 1 : 0;

    const value_size = amountValue / 1000000000000 > 0.999 ? 'trillion' : amountValue / 1000000000 > 0.999 ? 'billion' : 'million';
    const formattedNumber = (amountValue / division[value_size]).toFixed(decimalPlaces).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `$${formattedNumber} ${Object.keys(division)[Object.values(division).indexOf(division[value_size])]}`;
  }

  function cleanCompanyName(name) {
    if (!name) return null;
    const cleaned = name.trim();
    const fixes = {
      OpenaAI: 'OpenAI',
      OpneAI: 'OpenAI',
      Braodcom: 'Broadcom',
      'Nvidia ': 'Nvidia',
      Nvida: 'Nvidia',
      'Nvidia and others': 'Nvidia',
    };
    return fixes[cleaned] || cleaned;
  }

  function formatDate(dateInput) {
    if (!dateInput) return '';
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    let date;

    if (typeof dateInput === 'number') {
      // Excel serial number - convert to JS Date
      // Excel dates are days since Dec 30, 1899
      // 25569 = days between Dec 30, 1899 and Jan 1, 1970 (Unix epoch)
      date = new Date((dateInput - 25569) * 86400 * 1000);
    } else if (typeof dateInput === 'string') {
      // Parse MM/DD/YYYY string
      const parts = dateInput.split('/');
      if (parts.length === 3) {
        const month = months[parseInt(parts[0], 10) - 1];
        const day = parseInt(parts[1], 10);
        const year = parts[2];
        return `${month} ${day}, ${year}`;
      }
      // Try parsing as Date string
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return String(dateInput);
    }

    if (date && !Number.isNaN(date.getTime())) {
      // return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      return `${months[date.getMonth()]} ${date.getFullYear()}`;
    }
    return String(dateInput);
  }


  // Build nodes and links from JSON data
  // Each row in JSON is already a two-party deal
  const companies = new Set();
  const links = [];

  data.forEach((row, index) => {
    const partner1 = cleanCompanyName(row['Deal Partner 1']);
    const partner2 = cleanCompanyName(row['Deal Partner 2']);

    if (!partner1 || !partner2) return;

    companies.add(partner1);
    companies.add(partner2);

    const amount = parseDealAmount(row['Deal $ Amount']);
    console.log('amount', amount);

    links.push({
      id: `link-${index}`,
      source: partner1,
      target: partner2,
      amount,
      amountStr: row['Deal $ Amount'],
      details: row.description,
      date: row['Announcement Date'],
      allPartners: [partner1, partner2],
    });
  });

  console.log('Companies:', Array.from(companies));
  console.log('Links:', links);


  // Find all companies that have direct deals with Nvidia
  const nvidiaLinks = links.filter(link => link.source === 'Nvidia' || link.target === 'Nvidia');

  const innerRingCompanies = new Set();
  nvidiaLinks.forEach((link) => {
    if (link.source === 'Nvidia') innerRingCompanies.add(link.target);
    if (link.target === 'Nvidia') innerRingCompanies.add(link.source);
  });

  // For each inner ring company, find their satellite companies (non-Nvidia partners)
  const satelliteMap = {}; // Maps inner company -> array of satellite companies
  innerRingCompanies.forEach((company) => {
    const companyLinks = links.filter(link => (link.source === company || link.target === company)
      && link.source !== 'Nvidia' && link.target !== 'Nvidia');

    const satellites = new Set();
    companyLinks.forEach((link) => {
      if (link.source === company) satellites.add(link.target);
      if (link.target === company) satellites.add(link.source);
    });

    // Remove the company itself and any companies already in inner ring
    satellites.delete(company);
    innerRingCompanies.forEach(c => satellites.delete(c));

    satelliteMap[company] = Array.from(satellites);
  });

  // Build nodes with market cap and positioning info
  const nodes = [];
  const nodeMap = {};

  // Add market cap helper
  function getMarketCap(companyName) {
    const marketCapData = marketCap.find(m => m.COMPANY === companyName);
    return marketCapData && marketCapData.Price !== 'Not listed'
      ? parseFloat(marketCapData.Price)
      : null;
  }

  function getDescription(companyName) {
    const marketCapData = marketCap.find(m => m.COMPANY === companyName);
    return marketCapData && marketCapData.description;
  }

  // Nvidia (center)
  nodes.push({
    id: 'Nvidia',
    name: 'Nvidia',
    type: 'center',
    marketCapValue: getMarketCap('Nvidia'),
    parent: null,
  });
  nodeMap.Nvidia = nodes[nodes.length - 1];

  // Inner ring companies
  innerRingCompanies.forEach((company) => {
    nodes.push({
      id: company,
      name: company,
      type: 'inner',
      marketCapValue: getMarketCap(company),
      parent: 'Nvidia',
      description: getDescription(company),
    });
    nodeMap[company] = nodes[nodes.length - 1];
  });

  // Satellite companies
  Object.entries(satelliteMap).forEach(([parent, satellites]) => {
    satellites.forEach((satellite) => {
      // Only add if not already added as an inner ring company
      if (!nodeMap[satellite]) {
        nodes.push({
          id: satellite,
          name: satellite,
          type: 'satellite',
          marketCapValue: getMarketCap(satellite),
          parent,
          description: getDescription(satellite),
        });
        nodeMap[satellite] = nodes[nodes.length - 1];
      }
    });
  });

  // Scale for node size based on market cap
  const marketCapValues = nodes
    .map(d => d.marketCapValue)
    .filter(v => v !== null && v > 0);

  const maxMarketCap = Math.max(...marketCapValues) || 1;
  const minMarketCap = Math.min(...marketCapValues) || 0;

  console.log('maxMarketCap', maxMarketCap);
  console.log('minMarketCap', minMarketCap);

  // Create a unified scale for all nodes based on market cap
  const baseMinSize = isMobile ? 22 : 15;
  let baseMaxSize = isMobile ? 60 : 100;
  if (isWide) {
    baseMaxSize *= 1.22;
  }
  const noMarketCapSize = isMobile ? 14 : 15;

  // Use scalePow with exponent 0.4 for better visual differentiation
  // This shows more difference between small and large companies than scaleSqrt
  const marketCapScale = scaleSqrt()
    .exponent(0.4)
    .domain([0, maxMarketCap])
    .range([baseMinSize, baseMaxSize]);

  const multiplier = initialWidth > 330 ? 1.2 : 1;
  console.log('multiplier', multiplier);
  console.log('initialWidth', initialWidth);

  // Function to get node radius
  function getNodeRadius(d) {
    if (d.marketCapValue !== null && d.marketCapValue > 0) {
      return marketCapScale(d.marketCapValue) * multiplier;
    }
    // Fallback for companies without market cap data - use smaller fixed size
    return noMarketCapSize * multiplier;
  }

  // Helper to determine if node is a country
  function isCountry(d) {
    const countries = ['UK', 'South Korea'];
    return countries.includes(d.id);
  }


  // On desktop, use a narrower internal width to make the network fill more of the space
  const internalWidth = isMobile ? initialWidth : initialWidth;
  const internalHeight = initialHeight;

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight)
    .attr('viewBox', `0 0 ${initialWidth} ${initialHeight}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .attr('class', 'network-diagram');

  // Background
  svg.append('rect')
    .attr('width', initialWidth)
    .attr('height', initialHeight)
    .attr('fill', 'none');

  const g = svg.append('g').attr('class', 'main-group');
  if (isWide) {
    g.attr('transform', `translate(0, -${initialHeight * 0.05})`);
  }

  // Position nodes in the new layout
  const centerX = initialWidth / 2;
  const centerY = initialHeight / 2;
  const innerRingRadius = Math.min(initialWidth, initialHeight) * 0.28;
  const satelliteDistance = isMobile ? 100 : 140; // Increased distance from inner company to satellites

  // Position Nvidia at center-left (desktop) or top-center (mobile)
  const nvidiaNode = nodes.find(n => n.id === 'Nvidia');
  if (isMobile) {
    nvidiaNode.x = initialWidth * 0.55;
    nvidiaNode.y = initialHeight * 0.33;
  } else {
    // Desktop: Nvidia on left side, vertically centered
    nvidiaNode.x = initialWidth * 0.3;
    nvidiaNode.y = centerY;
  }
  nvidiaNode.fx = nvidiaNode.x;
  nvidiaNode.fy = nvidiaNode.y;

  // Position inner ring companies in circle around Nvidia
  const innerNodes = nodes.filter(n => n.type === 'inner');

  // No sorting - just use natural order from data
  const sortedInnerNodes = innerNodes;
  let openaiNode;
  sortedInnerNodes.forEach((node, i) => {
    // Special positioning for OpenAI - closer to Nvidia
    if (node.id === 'OpenAI') {
      if (isMobile) {
        node.x = (initialWidth * 0.5);
        node.y = initialHeight * 0.66;
      // }
      } else {
        node.x = initialWidth * 0.5;
        node.y = initialHeight * 0.4;
      }
      openaiNode = node;

      node.targetX = node.x;
      node.targetY = node.y;
      return;
    }

    if (node.id === 'Nebius') {
      if (isMobile) {
        node.x = (initialWidth * 0.3);
        node.y = initialHeight * 0.4;
      // }
      } else {
        node.x = initialWidth * 0.33;
        node.y = initialHeight * 0.8;
      }

      node.targetX = node.x;
      node.targetY = node.y;
      return;
    }

    if (node.id === 'Intel') {
      if (isMobile) {
        node.x = (initialWidth * 0.35);
        node.y = initialHeight * 0.3;
      // }
      } else {
        node.x = initialWidth * 0.2;
        node.y = initialHeight * 0.8;
      }

      node.targetX = node.x;
      node.targetY = node.y;
      return;
    }


    if (node.id === 'xAI') {
      if (isMobile) {
        node.x = (initialWidth * 0.23);
        node.y = initialHeight * 0.43;
      // }
      } else {
        node.x = initialWidth * 0.45;
        node.y = initialHeight * 0.55;
      }

      node.targetX = node.x;
      node.targetY = node.y;
      return;
    }

    if (node.id === 'South Korea') {
      if (isMobile) {
        node.x = (initialWidth * 0.8);
        node.y = initialHeight * 0.33;
      // }
      } else {
        node.x = initialWidth * 0.5;
        node.y = initialHeight * 0.25;
      }

      node.targetX = node.x;
      node.targetY = node.y;
      return;
    }


    // Arrange nodes in circle based on their order
    const otherNodes = sortedInnerNodes.filter(n => n.id !== 'OpenAI');
    const nodeIndex = otherNodes.findIndex(n => n.id === node.id);
    const angle = isMobile
      ? ((nodeIndex) / otherNodes.length) * 2 * Math.PI - 3 * Math.PI / 2
      : ((nodeIndex) / otherNodes.length) * 2 * Math.PI - 3.9 * Math.PI / 2; // Start from top

    node.x = nvidiaNode.x + innerRingRadius * Math.cos(angle);
    node.y = nvidiaNode.y + innerRingRadius * Math.sin(angle);
    node.targetX = node.x;
    node.targetY = node.y;
  });

  // Position satellite companies on the right side of the graphic
  const satellites = nodes.filter(n => n.type === 'satellite');

  // No sorting - use natural order
  const sortedSatellites = satellites;

  const rightX = centerX; // isMobile ? centerX + initialWidth * 0.5 : centerX + initialWidth * 0.35;
  const satelliteStartY = isMobile ? initialHeight * 1 : centerY - (sortedSatellites.length * 35) / 2; // Center vertically
  const satelliteSpacing = 10; // isMobile ? 70 : 80;

  sortedSatellites.forEach((node, i) => {
    // Position satellites around their parent companies
    const parentNode = nodeMap[node.parent];
    if (!parentNode) {
      // Fallback if no parent
      node.x = rightX;
      node.y = satelliteStartY + (i * satelliteSpacing);
    } else if (node.id === 'Microsoft') {
      if (isMobile) {
        node.x = (initialWidth * 0.2);
        node.y = initialHeight * 0.7;
      } else {
        node.x = (initialWidth * 0.6);
        node.y = initialHeight * 0.65;
      }
    } else if (node.id === 'Broadcom') {
      if (isMobile) {
        node.x = (initialWidth * 0.5);
        node.y = initialHeight * 0.8;
      } else {
        node.x = parentNode.x + (initialWidth * 0.1);
        node.y = initialHeight * 0.6;
      }
    } else if (node.id === 'Oracle') {
      if (isMobile) {
        node.x = (initialWidth * 0.55);
        node.y = initialHeight * 0.75;
      } else {
        node.x = (initialWidth * 0.5);
        node.y = initialHeight * 0.25;
      }
    } else if (node.id === 'AMD') {
      if (isMobile) {
        node.x = (initialWidth * 0.6);
        node.y = initialHeight * 0.6;
      } else {
        node.x = (initialWidth * 0.5);
        node.y = initialHeight * 0.25;
      }
    } else {
      // Position around parent in a circle
      const siblings = satellites.filter(n => n.parent === node.parent);
      const index = siblings.findIndex(n => n.id === node.id);
      const angle = (index / siblings.length) * 2 * Math.PI;
      const distance = isMobile ? 100 : 130;
      node.x = initialWidth * 0.6 + distance * Math.cos(angle);
      node.y = parentNode.y + distance * Math.sin(angle);
    }
    node.targetX = node.x;
    node.targetY = node.y;
  });

  // Add gentle force simulation to prevent overlaps
  const simulation = forceSimulation(nodes)
    .force('collide', forceCollide()
      .radius(d => getNodeRadius(d) + (isMobile ? 8 : 12))
      .strength(0.5))
    .force('x', forceX((d) => {
      // Pull satellites gently to the right
      if (d.type === 'satellite') {
        return d.x + initialWidth * 0.1;
      }
      // Pull countries gently to the left
      if (isCountry(d)) {
        return centerX - internalWidth * 0.25;
      }
      return d.targetX || d.x;
    }).strength((d) => {
      if (d.type === 'center') return 0;
      if (d.type === 'satellite') return 0.1; // Gentle pull to right
      if (isCountry(d)) return 0.1; // Gentle pull to left
      return 0.2;
    }))
    .force('y', forceY(d => d.targetY || d.y).strength(d => (d.type === 'center' ? 0 : 0.2)))
    .alphaDecay(0.03)
    .velocityDecay(0.4);

  // Convert link source/target from IDs to node object references
  // Only keep links that are relevant to our layout:
  // 1. Links between Nvidia and inner ring companies
  // 2. Links between inner ring companies and their satellites
  const relevantLinks = [];

  links.forEach((link) => {
    const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
    const targetId = typeof link.target === 'string' ? link.target : link.target.id;

    const sourceNode = nodeMap[sourceId];
    const targetNode = nodeMap[targetId];

    if (!sourceNode || !targetNode) return;

    // Keep if it's a Nvidia<->Inner or Inner<->Satellite connection
    const isRelevant = (sourceNode.type === 'center' && targetNode.type === 'inner')
      || (sourceNode.type === 'inner' && targetNode.type === 'center')
      || (sourceNode.type === 'inner' && targetNode.type === 'satellite')
      || (sourceNode.type === 'satellite' && targetNode.type === 'inner');

    if (isRelevant) {
      // Determine link color based on the non-Nvidia node type
      let linkColor;
      if (isCountry(sourceNode) || isCountry(targetNode)) {
        linkColor = linkColors.country;
      } else if (sourceNode.type === 'satellite' || targetNode.type === 'satellite') {
        linkColor = linkColors.satellite;
      } else {
        linkColor = linkColors.inner;
      }

      relevantLinks.push({
        ...link,
        source: sourceNode,
        target: targetNode,
        type: sourceNode.type,
        linkColor,
      });
    }
  });

  const linksGroup = g.append('g').attr('class', 'links');

  function getLinkPath(d) {
    // Create a curved path that bulges away from center
    const midX = (d.source.x + d.target.x) / 2;
    const midY = (d.source.y + d.target.y) / 2;

    // Vector from center to midpoint
    const dx = midX - centerX;
    const dy = midY - centerY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Normalize and push the control point away from center
    const curveStrength = d.type === 'satellite' ? 60 : 10; // Distance to push the curve outward
    const controlX = midX + (dx / dist) * curveStrength;
    const controlY = midY + (dy / dist) * curveStrength;

    // Create quadratic curve
    return `M ${d.source.x},${d.source.y} Q ${controlX},${controlY} ${d.target.x},${d.target.y}`;
  }

  const linkElements = linksGroup.selectAll('.link')
    .data(relevantLinks)
    .enter()
    .append('path')
    .attr('class', d => `link ${classify(d.source.id)} ${classify(d.target.id)}`)
    .attr('fill', 'none')
    .attr('stroke', d => d.linkColor)
    .attr('stroke-width', 1)
    .attr('stroke-opacity', 1)
    .attr('stroke-linecap', 'round')
    .attr('d', getLinkPath);

  // Draw nodes
  const nodesGroup = g.append('g').attr('class', 'nodes');

  const nodeElements = nodesGroup.selectAll('.node')
    .data(nodes)
    .enter()
    .append('g')
    .attr('class', d => `node node-${classify(d.id)} ${d.type}`)
    .attr('id', d => d.id.toLowerCase().replace(/ /g, '-'))
    .style('cursor', 'pointer');

  // Function to create hexagon path
  function getHexagonPath(radius) {
    const points = [];
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI / 4) * i - Math.PI / 2; // Start from top
      points.push([
        radius * Math.cos(angle),
        radius * Math.sin(angle),
      ]);
    }
    return `M ${points.map(p => p.join(',')).join(' L ')} Z`;
  }

  // Node shapes (circles for most, hexagons for countries)
  nodeElements.each(function (d) {
    const node = select(this);
    const radius = getNodeRadius(d);

    // Determine colors based on node type
    let fillColor; let
      strokeColor;
    if (d.type === 'center') {
      fillColor = nodeColors.nvidia;
      strokeColor = nodeStrokes.nvidia;
    } else if (isCountry(d)) {
      // Check countries before inner, since countries are type='inner'
      fillColor = nodeColors.country;
      strokeColor = nodeStrokes.country;
    } else if (d.type === 'inner') {
      fillColor = nodeColors.inner;
      strokeColor = nodeStrokes.inner;
    } else {
      fillColor = nodeColors.satellite;
      strokeColor = nodeStrokes.satellite;
    }

    // if (isCountry(d)) {
    //   // Hexagon for countries
    //   node.append('path')
    //     .attr('d', getHexagonPath(radius))
    //     .attr('fill', fillColor)
    //     .attr('stroke', strokeColor)
    //     .attr('stroke-width', 1.5);
    // } else

    if (d.type === 'center') {
      // Circle for companies
      node.append('circle')
        .attr('r', radius)
        .attr('fill', fillColor)
        .attr('stroke', strokeColor)
        .attr('stroke-width', 2);
    } else {
      // Circle for companies
      node.append('circle')
        .attr('r', radius)
        .attr('fill', fillColor)
        .attr('stroke', strokeColor)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', d => (d.marketCapValue === null && !isCountry(d) ? '4, 3' : 'none'));
    }
  });

  // Initial node positions (will be updated by simulation)
  nodeElements
    .attr('transform', d => `translate(${d.x}, ${d.y})`);

  // Node labels - show all labels
  const labelFontSize = 11;
  let labelElements;

  // if (labelElements) {
  //   // Mobile: labels below circles
  //   const labelsGroup = g.append('g').attr('class', 'labels-layer');

  //   labelElements = labelsGroup.selectAll('.node-label')
  //     .data(nodes)
  //     .enter()
  //     .append('text')
  //     .attr('class', d => `node-label mobile-label ${d.type}`)
  //     .attr('data-id', d => d.id)
  //     .attr('text-anchor', 'middle')
  //     .attr('dominant-baseline', 'hanging')
  //     .attr('fill', '#262626')
  //     .attr('font-size', d => (d.type === 'center' ? 14 : d.type === 'inner' ? 11 : 9))
  //     .attr('font-weight', d => (d.type === 'center' ? 700 : 600))
  //     .attr('opacity', 1) // Show all labels
  //     .attr('x', d => d.x)
  //     .attr('y', d => d.y + getNodeRadius(d) + 6)
  //     .text(d => d.name);
  // } else {
  // Desktop: labels inside circles for center/inner, below for satellites
  nodeElements.each(function (d) {
    const node = select(this);
    if (d.type === 'center') {
      node.append('text')
        .attr('class', 'node-label nvidia-label')
        .attr('text-anchor', 'middle')
        .attr('transform', 'translate(0, -10)')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#262626')
        .attr('font-size', 18)
        .attr('font-weight', 700)
        .text(d.name);

      node.append('text')
        .attr('class', 'node-label nvidia-label')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('transform', 'translate(0, 10)')
        .attr('fill', '#8e8e8e')
        .attr('font-size', isMobile ? 12 : 13)
        .attr('font-weight', 500)
        .html('Click on partners');

      node.append('text')
        .attr('class', 'node-label nvidia-label')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('transform', 'translate(0, 24)')
        .attr('fill', '#8e8e8e')
        .attr('font-size', isMobile ? 12 : 13)
        .attr('font-weight', 500)
        .html('to explore deals');
    } else {
      // Center and inner: label inside circle
      const text = node.append('text')
        .attr('class', 'node-label')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#262626')
        .attr('font-size', isMobile ? 11 : 12)
        .attr('font-weight', d.type === 'center' ? 700 : 600)
        .attr('filter', (d) => {
          let colour = nodeColors[d.type];
          if (isCountry(d)) {
            colour = nodeColors.country;
          }

          return `drop-shadow(0 1.5px 0 ${colour})
                         drop-shadow(0 -1.5px 0 ${colour})
                         drop-shadow(1.5px 0 0 ${colour})
                         drop-shadow(-1.5px 0 0 ${colour})
                         drop-shadow(0 0 1px ${colour})`;
        })
        .text(d.name);

      // Wrap long names
      const words = d.name.split(/\s+/);
      if (words.length > 1) {
        text.text('');
        words.forEach((word, i) => {
          text.append('tspan')
            .attr('x', 0)
            .attr('dy', i === 0 ? `-${(words.length - 1) * 0.5}em` : '1.1em')
            .text(word);
        });
      }
    }
  });
  // }

  // ============================================
  // SIMULATION TICK
  // ============================================

  simulation.on('tick', () => {
    // Update link positions
    linkElements.attr('d', getLinkPath);

    // Update node positions
    nodeElements.attr('transform', d => `translate(${d.x}, ${d.y})`);

    // Update mobile label positions
    if (isMobile && labelElements) {
      labelElements
        .attr('x', d => d.x)
        .attr('y', d => d.y + getNodeRadius(d) + 6);
    }
  });

  // ============================================
  // MODAL
  // ============================================

  const modal = container.append('div')
    .attr('class', 'deal-modal')
    .style('display', 'none')
    .style('z-index', '1000');

  const modalOverlay = container.append('div')
    .attr('class', 'modal-overlay')
    .attr('display', 'none')
    .style('z-index', '999');

  function showModal(nodeData) {
    const nodeDeals = relevantLinks.filter(link => link.source.id === nodeData.id || link.target.id === nodeData.id);

    if (nodeDeals.length === 0) return;
    console.log('nodeDeals', nodeDeals);
    const dealList = nodeDeals.map((deal) => {
      const partner = deal.source.id === nodeData.id ? deal.target.name : deal.source.name;
      return `
        <div style="margin-top: 20px; padding-top: 10px; border-bottom: 1px solid #eee;">
          <div style="font-weight: 600; margin-bottom: 5px;}">${deal.source.name}  ↔ ${deal.target.name}</div>
          ${deal.date ? `<div style="color: #666; font-size: 13px; margin-bottom: 10px;">${formatDate(deal.date)}</div>` : ''}
          ${deal.details ? `<div style="color: #666; font-size: 14px; line-height: 1.4;">${deal.details}</div>` : ''}
        </div>
      `;
    }).join('');

    modal.style('--color', nodeStrokes[nodeData.type])
      .html(`
      <div style="display: flex; justify-content: space-between; align-items: center;">
        <h3 style="margin: 0; font-size: 20px;">${nodeData.name === 'ARM' ? 'Arm' : nodeData.name === 'AMD' ? 'AMD (Advanced Micro Devices)' : nodeData.name}</h3>
        <button class="close-modal" style="background: none; border: none; font-size: 24px; cursor: pointer; padding: 0 5px; --color: ${isCountry(nodeData) ? nodeStrokes.country : nodeStrokes[nodeData.type]};">&times;</button>
      </div>
      <div style="display: flex; flex-direction: column; margin-bottom: 20px; border-bottom: 2px solid ${isCountry(nodeData) ? nodeStrokes.country : nodeStrokes[nodeData.type]}; padding-bottom: 15px;">
      ${nodeData.description ? `<p style="margin: 0; font-size: 14px;">${nodeData.description}</p>` : ''}
      </div>
      <div class="deal-list" style="font-size: 14px;">
        ${dealList}
      </div>
    `);
    // ${nodeData.marketCapValue ? `<p style="margin: 0; font-size: 14px; font-weight: 600;">Market Cap: ${parseDealAmount(nodeData.marketCapValue)}</p>` : ''}
    // ${deal.amountStr ? `<div style="margin-bottom: 5px;">${deal.amount}</div>` : ''}

    modal.style('display', 'block');
    modalOverlay.style('display', 'block');

    // Add close handlers
    modal.select('.close-modal').on('click', closeModal);
    modalOverlay.on('click', closeModal);
  }

  function closeModal() {
    modal.style('display', 'none');
    modalOverlay.style('display', 'none');
  }

  // ============================================
  // INTERACTIVITY
  // ============================================

  let selectedNode = null;

  function highlightNode(nodeId) {
    selectedNode = nodeId;

    let nodeIdArray = [nodeId];
    if (nodeId.includes('&')) {
      nodeIdArray = nodeId.split('&');
    }
    // hideOverlay();

    // Show reset instructions
    // resetInstructions
    //   .transition()
    //   .duration(300)
    //   .style('opacity', 1);
    const connectedNodes = new Set();
    const selectedNodeData = nodes.find(n => n.id === nodeId);
    if (nodeIdArray.length > 1) {
      nodeIdArray.forEach((nodeId) => {
        const selectedNodeData = nodes.find(n => n.id === nodeId);
        if (selectedNodeData) {
          connectedNodes.add(nodeId);
        }
      });
    } else {
      const selectedNodeData = nodes.find(n => n.id === nodeId);
      // Find connected nodes based on our hierarchy
      connectedNodes.add(nodeId);
    }

    // Find relevant links for this node
    relevantLinks.forEach((link) => {
      if (nodeIdArray) {
        nodeIdArray.forEach((nodeId) => {
          if (link.source.id === nodeId) connectedNodes.add(link.target.id);
          if (link.target.id === nodeId) connectedNodes.add(link.source.id);
        });
      } else {
        if (link.source.id === nodeId) connectedNodes.add(link.target.id);
        if (link.target.id === nodeId) connectedNodes.add(link.source.id);
      }
    });

    // Highlight links
    linkElements
      .transition()
      .duration(300)
      .attr('stroke', (d) => {
        const isConnected = nodeIdArray.includes(d.source.id) || nodeIdArray.includes(d.target.id);
        return isConnected ? d.linkColor : d.linkColor;
      })
      .attr('stroke-opacity', (d) => {
        const isConnected = nodeIdArray.includes(d.source.id) || nodeIdArray.includes(d.target.id);
        return isConnected ? 1 : 0.3;
      })
      .attr('stroke-width', (d) => {
        const isConnected = nodeIdArray.includes(d.source.id) || nodeIdArray.includes(d.target.id);
        return isConnected ? 3 : 1;
      });

    // Highlight nodes
    nodeElements.each(function (d) {
      const isSelected = nodeIdArray.includes(d.id);
      const isConnected = connectedNodes.has(d.id);
      const node = select(this);

      // Get the original fill color for this node
      let originalFillColor;
      if (isCountry(d)) {
        originalFillColor = nodeColors.country;
      } else if (d.type === 'center') {
        originalFillColor = nodeColors.nvidia;
      } else if (d.type === 'inner') {
        originalFillColor = nodeColors.inner;
      } else {
        originalFillColor = nodeColors.satellite;
      }

      node.select('circle')
        .transition()
        .duration(300)
        // .attr('r', isSelected ? getNodeRadius(d, 1.0) : getNodeRadius(d))
        // .attr('fill', isSelected || isConnected ? originalFillColor : '#bfbfbf')
        .attr('stroke-opacity', isSelected || isConnected ? 1 : 0.3)
        .attr('stroke-width', isSelected || isConnected ? 3 : 1);

      // Also handle hexagons for countries
      node.select('path')
        .transition()
        .duration(300)
        // .attr('fill', isSelected || isConnected ? originalFillColor : '#bfbfbf')
        .attr('stroke-opacity', isSelected || isConnected ? 1 : 0.3)
        .attr('stroke-width', isSelected || isConnected ? 2 : 1);
    });

    // Mobile: update labels
    if (isMobile && labelElements) {
      labelElements
        .transition()
        .duration(300)
        .attr('opacity', (d) => {
          const isSelected = d.id === nodeId;
          const isConnected = connectedNodes.has(d.id);
          // Always show center and inner, show satellites if selected/connected
          if (d.type === 'center' || d.type === 'inner') return 1;
          return isSelected || isConnected ? 1 : 0;
        });
    }
  }

  function resetHighlight() {
    selectedNode = null;

    // Hide reset instructions
    // resetInstructions
    //   .transition()
    //   .duration(300)
    //   .style('opacity', 0);

    linkElements
      .transition()
      .duration(300)
      .attr('stroke', d => d.linkColor)
      .attr('stroke-opacity', 1)
      .attr('stroke-width', 1);

    nodeElements.each(function (d) {
      const node = select(this);

      // // Get original color
      // let originalFillColor; let
      //   originalStrokeColor;
      // if (d.type === 'center') {
      //   originalFillColor = nodeColors.nvidia;
      //   originalStrokeColor = nodeStrokes.nvidia;
      // } else if (isCountry(d)) {
      //   // Check countries before inner
      //   originalFillColor = nodeColors.country;
      //   originalStrokeColor = nodeStrokes.country;
      // } else if (d.type === 'inner') {
      //   originalFillColor = nodeColors.inner;
      //   originalStrokeColor = nodeStrokes.inner;
      // } else {
      //   originalFillColor = nodeColors.satellite;
      //   originalStrokeColor = nodeStrokes.satellite;
      // }

      node.select('circle')
        .transition()
        .duration(300)
        .attr('stroke-opacity', 1)
        .attr('stroke-width', 2);
      // .attr('r', getNodeRadius(d))
      // .attr('fill', originalFillColor)
      // .attr('stroke', originalStrokeColor)
      // .attr('opacity', 1);

      node.select('path')
        .transition()
        .duration(300)
        // .attr('fill', originalFillColor)
        // .attr('stroke', originalStrokeColor)
        .attr('stroke-opacity', 1)
        .attr('stroke-width', 2);
    });

    if (isMobile && labelElements) {
      labelElements
        .transition()
        .duration(300)
        .attr('opacity', 1); // All labels visible
    }
  }

  // Click handlers for nodes
  nodeElements
    .on('click', (event, d) => {
      event.stopPropagation();
      // Show modal with deal details for inner and satellite nodes
      if (d.type === 'inner' || d.type === 'satellite') {
        showModal(d);
      }
    });


  // Hover handlers for nodes - just show visual highlight
  nodeElements
    .on('mouseenter', (event, d) => {
      highlightNode(d.id);
    })
    .on('mouseleave', (event, d) => {
      resetHighlight();
    });

  // Click on background to reset
  svg.on('click', () => {
    if (selectedNode) {
      resetHighlight();
    }
  });


  const legendItems = document.querySelectorAll('.legend-item');
  legendItems.forEach((item, index) => {
    item.onmouseover = () => {
      if (item.dataset.type === 'inner') {
        highlightNode('Nvidia');
      } else if (item.dataset.type === 'satellite') {
        highlightNode('OpenAI&Anthropic&Nebius');
      } else if (item.dataset.type === 'country') {
        highlightNode('UK&South Korea');
      }
    };
    item.onmouseleave = () => {
      resetHighlight();
    };
  });


  // ============================================
  // LEGEND / INSTRUCTIONS OVERLAY
  // ============================================

  // const overlay = container.append('div')
  //   .attr('class', 'instructions-overlay')
  //   .style('display', 'none');

  // overlay.append('div')
  //   .attr('class', 'instructions-box')
  //   .html('<span class="instructions-text">Hover over outer companies to see deals</span>');

  // // Function to hide overlay
  // function hideOverlay() {
  //   overlay
  //     .transition()
  //     .duration(300)
  //     .style('opacity', 0)
  //     .on('end', () => {
  //       overlay.style('display', 'none');
  //     });
  // }

  // // Click overlay to dismiss
  // overlay.on('click', hideOverlay);

  // Reset instructions (shown when a company is selected)
  // const resetInstructions = container.append('div')
  //   .attr('class', 'reset-instructions')
  //   .style('opacity', 1)
  //   .text('Click to read more');
}
